import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import { getUrl } from 'aws-amplify/storage';
import { isProduction } from '../config/environment';
import './Hansalinie.css';

const client = generateClient<Schema>({ authMode: 'iam' });

interface PartnerLogo {
  id: string;
  name: string;
  logo: string;
  fallbackLogo: string;
  courseUrl: string | null;
}

const Hansalinie: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const [partnerLogos, setPartnerLogos] = useState<PartnerLogo[]>([]);
  const [loading, setLoading] = useState(true);

  // Static partner configuration (names and course URLs)
  // These map to partner names in the database
  const partnerConfig = [
    { id: 'hansalinie', nameMatch: 'Hansalinie', fallbackLogo: '/assets/images/partners/hansalinie.svg', s3Logo: null, localLogo: null, courseUrl: null },
    { id: 'cloppenburg', nameMatch: 'Cloppenburg', fallbackLogo: '/assets/images/partners/clp.svg', s3Logo: null, localLogo: null, courseUrl: 'https://www.mintkitchen.de/courses/azubis-clp' },
    { id: 'diepholz', nameMatch: 'Diepholz', fallbackLogo: '/assets/images/partners/diepholz.svg', s3Logo: null, localLogo: null, courseUrl: 'https://www.mintkitchen.de/courses/azubis-dh' },
    { id: 'oldenburg', nameMatch: 'Oldenburg', fallbackLogo: '/assets/images/partners/wlo.svg', s3Logo: null, localLogo: null, courseUrl: 'https://www.mintkitchen.de/courses/azubis-ol' },
    { id: 'wigos', nameMatch: 'WIGOS', fallbackLogo: '/assets/images/partners/wigos.svg', s3Logo: 'public/partners/wirtschaftsf-rderung-osnabr-ck/1763037949230_wigos_neu.svg', localLogo: '/assets/images/partners/1763037949230_wigos_neu.svg', courseUrl: 'https://www.mintkitchen.de/courses/azubis-os' },
    { id: 'vechta', nameMatch: 'Vechta', fallbackLogo: '/assets/images/partners/vechta.svg', s3Logo: null, localLogo: null, courseUrl: 'https://www.mintkitchen.de/courses/azubis-vec' }
  ];

  // Load partner logos from database
  useEffect(() => {
    const loadPartnerLogos = async () => {
      console.log('🔍 Loading partner logos from database...');
      setLoading(true);
      
      try {
        // Fetch all partners from database
        const { data: partners } = await client.models.Partner.list();
        console.log(`✅ Fetched ${partners?.length || 0} partners from database`);

        // Build partner logos array by matching with config
        const logos: PartnerLogo[] = [];
        
        for (const config of partnerConfig) {
          let logoUrl: string | null = null;
          let partnerName = config.nameMatch;

          // Priority 1: Use environment-specific logo if available
          // In production: use S3 logo
          // In dev/sandbox: use local logo for faster loading
          if (isProduction() && config.s3Logo) {
            console.log(`📂 [PRODUCTION] Using S3 logo for ${config.nameMatch}: ${config.s3Logo}`);
            try {
              const { url } = await getUrl({ path: config.s3Logo });
              logoUrl = url.toString();
              console.log(`✅ Generated S3 URL for ${config.nameMatch}: ${logoUrl.substring(0, 80)}...`);
            } catch (error) {
              console.error(`❌ Failed to get S3 URL for ${config.nameMatch}, will try database:`, error);
              logoUrl = null;
            }
          } else if (!isProduction() && config.localLogo) {
            console.log(`📂 [DEV/SANDBOX] Using local logo for ${config.nameMatch}: ${config.localLogo}`);
            logoUrl = config.localLogo;
          } else if (config.s3Logo) {
            // Fallback: if no localLogo in dev, try S3 anyway
            console.log(`📂 Using S3 logo from config for ${config.nameMatch}: ${config.s3Logo}`);
            try {
              const { url } = await getUrl({ path: config.s3Logo });
              logoUrl = url.toString();
              console.log(`✅ Generated S3 URL for ${config.nameMatch}: ${logoUrl.substring(0, 80)}...`);
            } catch (error) {
              console.error(`❌ Failed to get S3 URL for ${config.nameMatch}, will try database:`, error);
              logoUrl = null;
            }
          }

          // Priority 2: Find matching partner in database (case-insensitive name match)
          if (!logoUrl) {
            const dbPartner = partners?.find((p: any) => 
              p.name.toLowerCase().includes(config.nameMatch.toLowerCase()) ||
              config.nameMatch.toLowerCase().includes(p.name.toLowerCase())
            );

            if (dbPartner && dbPartner.logoPath) {
              partnerName = dbPartner.name;
              console.log(`📂 Found partner in DB: ${dbPartner.name}, logo: ${dbPartner.logoPath}`);
              
              // Resolve S3 URL for the logo
              try {
                const logoPath = dbPartner.logoPath;

                if (logoPath.startsWith('http') || logoPath.startsWith('/')) {
                  // Already a URL or local path
                  logoUrl = logoPath;
                } else {
                  // It's an S3 key, resolve it
                  const s3Path = logoPath.startsWith('public/') ? logoPath : `public/${logoPath}`;
                  const { url } = await getUrl({ path: s3Path });
                  logoUrl = url.toString();
                }
                if (logoUrl) {
                  console.log(`✅ Loaded DB logo for ${dbPartner.name}: ${logoUrl.substring(0, 80)}...`);
                }
              } catch (error) {
                console.error(`❌ Failed to resolve S3 URL for ${dbPartner.name}:`, error);
                logoUrl = null;
              }
            }
          }

          // Priority 3: Use fallback logo
          if (!logoUrl) {
            console.log(`⚠️  Using fallback logo for '${config.nameMatch}'`);
            logoUrl = config.fallbackLogo;
          }

          logos.push({
            id: config.id,
            name: partnerName,
            logo: logoUrl,
            fallbackLogo: config.fallbackLogo,
            courseUrl: config.courseUrl
          });
        }

        setPartnerLogos(logos);
        console.log('✅ All partner logos loaded');
      } catch (error) {
        console.error('❌ Error loading partner logos:', error);
        // Use all fallback logos
        setPartnerLogos(partnerConfig.map(config => ({
          id: config.id,
          name: config.nameMatch,
          logo: config.fallbackLogo,
          fallbackLogo: config.fallbackLogo,
          courseUrl: config.courseUrl
        })));
      } finally {
        setLoading(false);
      }
    };

    loadPartnerLogos();
  }, []);


  // Handle partner logo click
  const handlePartnerClick = (courseUrl: string | null) => {
    if (courseUrl) {
      window.open(courseUrl, '_blank');
    }
  };

  // Helper to get logo URL by partner ID
  const getPartnerLogo = (partnerId: string): string => {
    const partner = partnerLogos.find(p => p.id === partnerId);
    return partner?.logo || '/assets/images/partners/placeholder.svg';
  };


  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        heroRef.current.style.minHeight = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16;
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
      // Set min-height for 2x3 logo grid
      const isSmallMobile = window.matchMedia('(max-width: 480px)').matches;
      heroRef.current.style.minHeight = isSmallMobile ? '600px' : '600px';
      
      // Force layout recalculation to fix resize issues
      const grid = heroRef.current.querySelector('.hansalinie-partners-grid');
      if (grid) {
        void (grid as HTMLElement).offsetHeight;
      }
    };

    applyMobileHeroOffset();
    
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(applyMobileHeroOffset, 100);
    };
    
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  return (
    <div className="page hansalinie-page">
      <section
        ref={(el) => { heroRef.current = el; }}
        className="page-header hansalinie-hero"
        data-section="hansalinie-hero"
      >
        <div className="container">
          <div className="hansalinie-hero-content">
            <h1 className="hansalinie-hero-title">KI-Kurs für Azubis</h1>
            <p className="hansalinie-hero-subtitle">
              in Kooperation mit der Wachstumsregion Hansalinie e.V.
            </p>
            
            {/* Partner Logos in Hero */}
            <div className="hansalinie-partners-grid">
                  {loading ? (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', color: 'white', padding: '2rem' }}>
                      Lade Partner-Logos...
                    </div>
                  ) : (
                    partnerLogos.map((partner, index) => (
                      <React.Fragment key={partner.id}>
                        <div
                          className={`hansalinie-partner-item ${partner.courseUrl ? 'clickable' : ''}`}
                          title={partner.courseUrl ? `Zu ${partner.name} Kurs` : partner.name}
                          onClick={() => handlePartnerClick(partner.courseUrl)}
                        >
                    <img
                      src={partner.logo}
                      alt={`${partner.name} Logo`}
                      className="hansalinie-partner-logo-small"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        // Use fallback logo if main logo fails to load
                        if (target.src !== partner.fallbackLogo) {
                          console.log(`⚠️  Logo failed for ${partner.name}, using fallback: ${partner.fallbackLogo}`);
                          target.src = partner.fallbackLogo;
                        }
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </div>
                        {index === 0 && (
                          <div className="hansalinie-divider"></div>
                        )}
                      </React.Fragment>
                    ))
                  )}
            </div>
          </div>
        </div>
      </section>

          {/* Cloppenburg Partner Section */}
          <section className="hansalinie-partner-section" data-section="hansalinie-cloppenburg">
            <div className="container">
              <div className="hansalinie-partner-content">
                <div className="hansalinie-partner-text">
                  <h2 className="hansalinie-partner-title">Landkreis <span className="hansalinie-highlight">Cloppenburg</span></h2>
                  <h3 className="hansalinie-partner-info-subtitle">Partnerinformationen</h3>
                  <div className="hansalinie-partner-info">
                    <p className="hansalinie-partner-organization">Landkreis Cloppenburg</p>
                    <p className="hansalinie-partner-address">Eschstraße 29</p>
                    <p className="hansalinie-partner-address">49661 Cloppenburg</p>
                    <p className="hansalinie-partner-contact-person">Ansprechpartner: Paul Bauer</p>
                    <p className="hansalinie-partner-contact">Telefon: +49 (0) 44 71 / 15 - 408</p>
                  </div>
                  <div className="hansalinie-partner-buttons">
                    <a 
                      href="https://www.lkclp.de/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Zur Partner-Webseite
                    </a>
                    <a 
                      href="mailto:P.Bauer@lkclp.de?subject=KI-Kurs für Azubis in der Hansalinie: Q1/2026"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Email an Partner
                    </a>
                    <a 
                      href="https://www.mintkitchen.de/courses/azubis-clp"
                      className="hansalinie-partner-button hansalinie-partner-button-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zum KI-Kurs
                    </a>
                  </div>
                </div>
                <div className="hansalinie-partner-logo">
                  <a 
                    href="https://www.lkclp.de/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Zur Landkreis Cloppenburg Webseite"
                  >
                    <img
                      src={getPartnerLogo('cloppenburg')}
                      alt="Landkreis Cloppenburg Logo"
                      className="hansalinie-partner-logo-large"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDIwMCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
                        target.alt = 'Partner Logo Placeholder';
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Diepholz Partner Section */}
          <section className="hansalinie-partner-section" data-section="hansalinie-diepholz">
            <div className="container">
              <div className="hansalinie-partner-content">
                <div className="hansalinie-partner-text">
                  <h2 className="hansalinie-partner-title">Landkreis <span className="hansalinie-highlight">Diepholz</span></h2>
                  <h3 className="hansalinie-partner-info-subtitle">Partnerinformationen</h3>
                  <div className="hansalinie-partner-info">
                    <p className="hansalinie-partner-organization">Landkreis Diepholz</p>
                    <p className="hansalinie-partner-address">Niedersachsenstraße 2</p>
                    <p className="hansalinie-partner-address">49356 Diepholz</p>
                    <p className="hansalinie-partner-contact-person">Ansprechpartner: Sven Mörker</p>
                    <p className="hansalinie-partner-contact">Telefon: +49 (0) 54 41 / 976 - 1454</p>
                  </div>
                  <div className="hansalinie-partner-buttons">
                    <a 
                      href="https://www.diepholz.de/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Zur Partner-Webseite
                    </a>
                    <a 
                      href="mailto:sven.moerker@diepholz.de?subject=KI-Kurs für Azubis in der Hansalinie: Q1/2026"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Email an Partner
                    </a>
                    <a 
                      href="https://www.mintkitchen.de/courses/azubis-dh"
                      className="hansalinie-partner-button hansalinie-partner-button-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zum KI-Kurs
                    </a>
                  </div>
                </div>
                <div className="hansalinie-partner-logo">
                  <a 
                    href="https://www.diepholz.de/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Zur Landkreis Diepholz Webseite"
                  >
                    <img
                      src={getPartnerLogo('diepholz')}
                      alt="Landkreis Diepholz Logo"
                      className="hansalinie-partner-logo-large"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDIwMCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
                        target.alt = 'Partner Logo Placeholder';
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Oldenburg Partner Section */}
          <section className="hansalinie-partner-section" data-section="hansalinie-oldenburg">
            <div className="container">
              <div className="hansalinie-partner-content">
                <div className="hansalinie-partner-text">
                  <h2 className="hansalinie-partner-title">Landkreis <span className="hansalinie-highlight">Oldenburg</span></h2>
                  <h3 className="hansalinie-partner-info-subtitle">Partnerinformationen</h3>
                  <div className="hansalinie-partner-info">
                    <p className="hansalinie-partner-organization">WLO Wirtschaftsförderungsgesellschaft für den Landkreis Oldenburg mbH</p>
                    <p className="hansalinie-partner-address">Delmenhorster Straße 6</p>
                    <p className="hansalinie-partner-address">27793 Wildeshausen</p>
                    <p className="hansalinie-partner-contact">Telefon: +49 (0) 44 31 / 85 - 438</p>
                  </div>
                  <div className="hansalinie-partner-buttons">
                    <a 
                      href="https://www.wlo.de/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Zur Partner-Webseite
                    </a>
                    <a 
                      href="mailto:info@wlo.de?subject=KI-Kurs für Azubis in der Hansalinie: Q1/2026"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Email an Partner
                    </a>
                    <a 
                      href="https://www.mintkitchen.de/courses/azubis-ol"
                      className="hansalinie-partner-button hansalinie-partner-button-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zum KI-Kurs
                    </a>
                  </div>
                </div>
                <div className="hansalinie-partner-logo">
                  <a 
                    href="https://www.wlo.de/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Zur WLO Landkreis Oldenburg Webseite"
                  >
                    <img
                      src={getPartnerLogo('oldenburg')}
                      alt="WLO Landkreis Oldenburg Logo"
                      className="hansalinie-partner-logo-large"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDIwMCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
                        target.alt = 'Partner Logo Placeholder';
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Osnabrück Partner Section */}
          <section className="hansalinie-partner-section" data-section="hansalinie-osnabrueck">
            <div className="container">
              <div className="hansalinie-partner-content">
                <div className="hansalinie-partner-text">
                  <h2 className="hansalinie-partner-title">Landkreis <span className="hansalinie-highlight">Osnabrück</span></h2>
                  <h3 className="hansalinie-partner-info-subtitle">Partnerinformationen</h3>
                  <div className="hansalinie-partner-info">
                    <p className="hansalinie-partner-organization">WIGOS - Wirtschaftsförderungsgesellschaft Osnabrücker Land mbH</p>
                    <p className="hansalinie-partner-address">Am Schölerberg 1</p>
                    <p className="hansalinie-partner-address">49082 Osnabrück</p>
                    <p className="hansalinie-partner-contact-person">Ansprechpartnerin: Andrea Frosch</p>
                    <p className="hansalinie-partner-contact">Telefon: +49 (0) 54 1 / 501 - 4903</p>
                  </div>
                  <div className="hansalinie-partner-buttons">
                    <a 
                      href="https://www.wigos.de/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Zur Partner-Webseite
                    </a>
                    <a 
                      href="mailto:Frosch@wigos.de?subject=KI-Kurs für Azubis in der Hansalinie: Q1/2026"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Email an Partner
                    </a>
                    <a 
                      href="https://www.mintkitchen.de/courses/azubis-os"
                      className="hansalinie-partner-button hansalinie-partner-button-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zum KI-Kurs
                    </a>
                  </div>
                </div>
                <div className="hansalinie-partner-logo">
                  <a 
                    href="https://www.wigos.de/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Zur WIGOS Webseite"
                  >
                    <img
                      src={getPartnerLogo('wigos')}
                      alt="WIGOS Logo"
                      className="hansalinie-partner-logo-large"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDIwMCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
                        target.alt = 'Partner Logo Placeholder';
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Vechta Partner Section */}
          <section className="hansalinie-partner-section" data-section="hansalinie-vechta">
            <div className="container">
              <div className="hansalinie-partner-content">
                <div className="hansalinie-partner-text">
                  <h2 className="hansalinie-partner-title">Landkreis <span className="hansalinie-highlight">Vechta</span></h2>
                  <h3 className="hansalinie-partner-info-subtitle">Partnerinformationen</h3>
                  <div className="hansalinie-partner-info">
                    <p className="hansalinie-partner-organization">Landkreis Vechta</p>
                    <p className="hansalinie-partner-address">Ravensberger Straße 20</p>
                    <p className="hansalinie-partner-address">49377 Vechta</p>
                    <p className="hansalinie-partner-contact-person">Ansprechpartnerin: Ulrike Meyer</p>
                    <p className="hansalinie-partner-contact">Telefon: +49 (0) 44 41 / 898 - 2611</p>
                  </div>
                  <div className="hansalinie-partner-buttons">
                    <a 
                      href="https://www.landkreis-vechta.de/wirtschaft-und-arbeit/wirtschaftsfoerderung.html" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Zur Partner-Webseite
                    </a>
                    <a 
                      href="mailto:2611@landkreis-vechta.de?subject=KI-Kurs für Azubis in der Hansalinie: Q1/2026"
                      className="hansalinie-partner-button hansalinie-partner-button-secondary"
                    >
                      Email an Partner
                    </a>
                    <a 
                      href="https://www.mintkitchen.de/courses/azubis-vec"
                      className="hansalinie-partner-button hansalinie-partner-button-primary"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Zum KI-Kurs
                    </a>
                  </div>
                </div>
                <div className="hansalinie-partner-logo">
                  <a 
                    href="https://www.landkreis-vechta.de/wirtschaft-und-arbeit/wirtschaftsfoerderung.html" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Zur Landkreis Vechta Webseite"
                  >
                    <img
                      src={getPartnerLogo('vechta')}
                      alt="Landkreis Vechta Logo"
                      className="hansalinie-partner-logo-large"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = `data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDIwMCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMTQwIiBmaWxsPSIjRjVGNUY1IiBzdHJva2U9IiNFMEUwRTAiLz4KPHR5cGU+TG9nbzwvdHlwZT4KPC9zdmc+`;
                        target.alt = 'Partner Logo Placeholder';
                      }}
                      onDragStart={(e) => e.preventDefault()}
                      onContextMenu={(e) => e.preventDefault()}
                      draggable={false}
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>

    </div>
  );
};

export default Hansalinie;
